import { isResource } from '@medplum/core'
import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import type { SeenMedplumClient } from '@seen/core/client'
import { getDisplayString } from '@seen/core/display'
import { extractElationId } from '@seen/core/elation'
import {
  fhirSearchUrl,
  matchesSearchRequest,
  parseSearchRequest,
  type Schema,
} from '@seen/core/medplum'
import { Participant } from '@seen/core/models/patients/participant'
import { getReferenceString } from '@seen/core/references'
import { normalizeQuery, type QueryTypes } from '@seen/core/search'
import { isOfType, recordKeys, recordValues } from '@seen/core/utils'
import { type Query, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { mapValues } from 'lodash-es'
import { useEffect, useState } from 'react'
import { extractResourcesFromQuery } from '#src/cache.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'
import { useSchemas } from '#src/hooks/useSchemas.ts'
import {
  FuzzySearcher,
  type Indexer,
  type IndexerRecord,
} from '#src/search/FuzzySearcher.ts'
import { SEARCH_WEIGHT } from '#src/search/weight.ts'

export type CacheSearchParam<T extends ResourceType> = QueryTypes & {
  indexers?: Indexer<T>[]
}

export type CacheSearchQuery<T extends ResourceType> = {
  [K in T]: CacheSearchParam<K>
}
type ResourceRecord<T extends ResourceType> = Record<string, ExtractResource<T>>

// Be wary of passing user input into query. matchesSearchRequest will be called based on query and its comparisons are based on string.includes for string parameters and string equality for Identifier and CodeableConcept. For example, { "name": "Ch Ph" } will not match "Charles Pham" as a name, and { "code:text": "med" } will not match "medication".
// TODO: Consider improving matchesSearchRequest to gracefully handle these cases.
export function useCacheSearcher<T extends ResourceType = ResourceType>(
  query: CacheSearchQuery<T>
): FuzzySearcher<T> {
  const searcher = useFuzzySearcher({ query })
  useSyncWithCache(searcher, { query })
  return searcher
}

function useFuzzySearcher<T extends ResourceType = ResourceType>({
  query,
}: {
  query: CacheSearchQuery<T>
}): FuzzySearcher<T> {
  const indexerRecord = useIndexerRecord(query)
  const [searcher] = useState(new FuzzySearcher<T>({ indexerRecord }))
  useEffect(
    () => searcher.updateIndexerRecord(indexerRecord),
    [indexerRecord, searcher]
  )
  return searcher
}

function useIndexerRecord<T extends ResourceType>(
  query: CacheSearchQuery<T>
): IndexerRecord<T> {
  return useMemoValue(
    mapValues(query, ({ indexers = [] }) => [
      { getKey: (r) => r.id ?? '', weight: SEARCH_WEIGHT.RESOURCE_ID },
      { getKey: getDisplayString, weight: SEARCH_WEIGHT.RESOURCE_NAME },
      {
        getKey: (r) => {
          const display = getDisplayString(r)
          const collapsed = display.replace(/\s+/g, '')
          return collapsed === display ? '' : collapsed
        },
        weight: SEARCH_WEIGHT.RESOURCE_NAME,
      },
      {
        getKey: (r) => {
          if ('identifier' in r) {
            if (Array.isArray(r.identifier)) {
              return extractElationId(r)?.toString() ?? ''
            }
            return r.identifier?.value ?? ''
          }
          return ''
        },
        weight: SEARCH_WEIGHT.RESOURCE_ID,
      },
      {
        getKey: (r) => {
          if ('telecom' in r) {
            const telecom = r.telecom?.find((t) => t.system === 'phone')
            return telecom?.value ?? ''
          }
          return ''
        },
        weight: SEARCH_WEIGHT.PHONE_NUMBER,
      },
      {
        getKey: (r) => {
          if ('telecom' in r) {
            const telecom = r.telecom?.find((t) => t.system === 'email')
            return telecom?.value ?? ''
          }
          return ''
        },
        weight: SEARCH_WEIGHT.EMAIL,
      },
      {
        getKey: (r) => {
          if (isOfType(r, 'Patient')) {
            const ppt = Participant.fromFhirResource(r)
            return ppt.seenMRN ?? ''
          }
          return ''
        },
        weight: SEARCH_WEIGHT.SEEN_MRN,
      },
      {
        getKey: (r) => {
          if ('birthDate' in r && r.birthDate) {
            return format(parseISO(r.birthDate), 'MMMM d yyyy')
          }
          return ''
        },
        weight: SEARCH_WEIGHT.BIRTH_DATE_MMMM_D_YYYY,
      },
      {
        getKey: (r) => {
          if ('birthDate' in r && r.birthDate) {
            return format(parseISO(r.birthDate), 'M/d/yyyy')
          }
          return ''
        },
        weight: SEARCH_WEIGHT.BIRTH_DATE_M_D_YYYY,
      },
      {
        getKey: (r) => {
          if (isOfType(r, 'Patient')) {
            const ppt = Participant.fromFhirResource(r)
            if (ppt.nickname) {
              return ppt.nickname
            }
          }
          return ''
        },
        weight: SEARCH_WEIGHT.NICKNAME,
      },
      {
        getKey: (r) =>
          getDisplayString(r)
            .match(/\b(\w)/g)
            ?.join('') ?? '',
        weight: SEARCH_WEIGHT.RESOURCE_INITIALS,
      },
      ...indexers,
    ]) as IndexerRecord<T>
  )
}

function useSyncWithCache<T extends ResourceType = ResourceType>(
  searcher: FuzzySearcher<T>,
  { query }: { query: CacheSearchQuery<T> }
): void {
  const queryRecord = useMemoValue(
    mapValues(
      query,
      ({ indexers: _, ...queryTypes }) => queryTypes as QueryTypes
    )
  )
  useSubscribeToCache(searcher, { queryRecord })
  useSyncInitialDataFromCache(searcher, { queryRecord })
}

function useSubscribeToCache<T extends ResourceType = ResourceType>(
  searcher: FuzzySearcher<T>,
  { queryRecord }: { queryRecord: Record<T, QueryTypes> }
): void {
  const queryClient = useQueryClient()
  const medplum = useMedplum()
  const { data: schemas } = useSchemas(recordKeys(queryRecord))

  useEffect(() => {
    if (!schemas) {
      return
    }
    const cache = queryClient.getQueryCache()
    const unsubscribe = cache.subscribe((event) => {
      if (!(event.type === 'added' || event.type === 'updated')) {
        return
      }
      const validResourcesRecord = extractValidResources(event.query, {
        queryRecord,
        schemas,
        medplum,
      })
      searcher.addResources(recordValues(validResourcesRecord))
    })
    return unsubscribe
  }, [schemas, queryClient, queryRecord, medplum, searcher])
}

function useSyncInitialDataFromCache<T extends ResourceType = ResourceType>(
  searcher: FuzzySearcher<T>,
  { queryRecord }: { queryRecord: Record<T, QueryTypes> }
): void {
  const queryClient = useQueryClient()
  const medplum = useMedplum()
  const { data: schemas } = useSchemas(recordKeys(queryRecord))

  useEffect(() => {
    if (!schemas) {
      return
    }
    const cachedQueries = queryClient.getQueryCache().findAll()
    const validResourcesRecord = cachedQueries.reduce<ResourceRecord<T>>(
      (acc, query) =>
        Object.assign(
          acc,
          extractValidResources(query, { queryRecord, schemas, medplum })
        ),
      {}
    )
    searcher.setResources(recordValues(validResourcesRecord))
  }, [queryClient, schemas, queryRecord, medplum, searcher])
}

function extractValidResources<T extends ResourceType = ResourceType>(
  query: Query<unknown, Error, unknown>,
  {
    queryRecord,
    schemas,
    medplum,
  }: {
    queryRecord: Record<T, QueryTypes>
    schemas: { [K in T]: Schema<K> }
    medplum: SeenMedplumClient
  }
): ResourceRecord<T> {
  const resources = extractResourcesFromQuery(query)
  return resources.reduce<ResourceRecord<T>>((acc, item) => {
    if (shouldAdd(item, { schemas, queryRecord, medplum })) {
      acc[getReferenceString(item)] = item
    }
    return acc
  }, {})
}

function shouldAdd<T extends ResourceType = ResourceType>(
  item: unknown,
  {
    schemas,
    queryRecord,
    medplum,
  }: {
    schemas: { [K in T]: Schema<K> } | undefined
    queryRecord: Record<T, QueryTypes>
    medplum: SeenMedplumClient
  }
): item is ExtractResource<T> {
  if (!schemas || !isResource(item) || !(item.resourceType in queryRecord)) {
    return false
  }
  const resource = item as ExtractResource<T>
  const searchUrl = fhirSearchUrl(
    resource.resourceType,
    normalizeQuery(queryRecord[resource.resourceType]),
    medplum
  )
  const schema = schemas[resource.resourceType]
  return matchesSearchRequest(
    resource,
    parseSearchRequest(searchUrl, schema),
    schema
  )
}
